import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import BundleSection from '../components/bundle/BundleSection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  pending_payment: 'bg-amber-100 text-amber-800',
  past_due: 'bg-orange-100 text-orange-800',
  expired: 'bg-gray-100 text-gray-700',
  canceled: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  active: 'Active',
  pending_payment: 'Awaiting payment',
  past_due: 'Past due',
  expired: 'Expired',
  canceled: 'Canceled',
};

/**
 * Full-page Engagement Bundle detail — same role as SubscriptionDetail for
 * per-site plans. Management actions live in BundleSection (shared with the
 * account page); this page is the dedicated entry from the Bundles list.
 */
const BundleDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [account, setAccount] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [tierLabel, setTierLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      // Drop prior account/bundle before the new fetch so a failed reload after
      // accountId changes cannot leave the header on the previous account while
      // BundleSection mounts against the new id.
      setAccount(null);
      setBundle(null);
      setTierLabel(null);
      try {
        const [detailRes, catalogRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/admin/bundles/${accountId}`),
          axios.get(`${API_BASE_URL}/api/admin/bundles/tiers`).catch(() => ({ data: { tiers: [] } })),
        ]);
        if (cancelled) return;
        setAvailable(true);
        setAccount(detailRes.data.account || null);
        setBundle(detailRes.data.bundle || null);
        const tierKey = detailRes.data.bundle?.tier;
        const match = (catalogRes.data.tiers || []).find((t) => t.key === tierKey);
        setTierLabel(match?.displayName || tierKey || null);
      } catch (error) {
        if (cancelled) return;
        setAccount(null);
        setBundle(null);
        setTierLabel(null);
        if (error.response?.status === 404) {
          // Feature off vs unknown account — the body distinguishes them.
          if (error.response?.data?.error === 'Account not found') {
            setAvailable(true);
          } else {
            setAvailable(false);
          }
        } else {
          console.error('Error loading bundle detail:', error);
          toast.error('Failed to load bundle details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [accountId]);

  if (!available) {
    return (
      <AdminLayout>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <SparklesIcon className="h-10 w-10 text-gray-300 mx-auto" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900">
            Engagement Bundle is not enabled
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Set <code className="px-1 bg-gray-100 rounded">FEATURE_ENGAGEMENT_BUNDLE=true</code> on
            the API to manage bundles from the backoffice.
          </p>
          <button
            type="button"
            onClick={() => navigate('/bundles')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            Back to Bundles
          </button>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  if (!account) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Bundle account not found</p>
          <button
            type="button"
            onClick={() => navigate('/bundles')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            Back to Bundles
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <button
          type="button"
          onClick={() => navigate('/bundles')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Bundles
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-gray-900">Engagement Bundle</h1>
            {tierLabel && (
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-indigo-100 text-indigo-800 capitalize">
                {tierLabel}
              </span>
            )}
            {bundle ? (
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${STATUS_STYLES[bundle.status] || STATUS_STYLES.expired}`}>
                {STATUS_LABELS[bundle.status] || bundle.status}
              </span>
            ) : (
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-700">
                Legacy billing
              </span>
            )}
          </div>
          <Link
            to={`/accounts/${accountId}`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg transition-colors text-sm font-medium"
          >
            <BuildingOfficeIcon className="h-4 w-4" />
            Open account
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-5 mb-6">
          <div className="text-xs uppercase tracking-wide text-gray-500">Account</div>
          <Link
            to={`/accounts/${accountId}`}
            className="mt-1 inline-flex items-center gap-2 text-lg font-semibold text-indigo-600 hover:text-indigo-800"
          >
            <BuildingOfficeIcon className="h-5 w-5" />
            {account.name || accountId}
          </Link>
          <div className="mt-1 text-xs text-gray-500 font-mono">{accountId}</div>
        </div>

        <BundleSection accountId={accountId} />
      </div>
    </AdminLayout>
  );
};

export default BundleDetail;
